import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MdCheck, MdDelete } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { Checkbox, InputField, SearchInput, Select, Textarea } from "components/fields";
import { useConfirm } from "components/dialog/ConfirmProvider";
import groupsService from "services/groupsService";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { GROUP_TYPE_OPTIONS, swatchTextClass } from "./groupDisplay";

// Stored on the group and used as its identity color across the app.
const COLOR_OPTIONS = [
  { value: "#6366F1", label: "Indigo" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#EF4444", label: "Red" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#10B981", label: "Green" },
  { value: "#3B82F6", label: "Blue" },
  { value: "#06B6D4", label: "Cyan" },
];

const EMPTY_GROUP = {
  name: "",
  description: "",
  groupCategory: "people_management",
  groupType: "custom",
  color: COLOR_OPTIONS[0].value,
  // Heartbeat-specific fields, round-tripped unchanged
  focusArea: "",
  meetingDay: "",
  meetingTime: "",
  location: "",
  maxCapacity: "",
  leaderId: "",
  campusId: "",
  startDate: "",
  endDate: "",
  notes: "",
  memberIds: [],
};

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Group name is required"),
});

const toFormValues = (group) => ({
  name: group.name,
  description: group.description || "",
  groupCategory: group.groupCategory || "people_management",
  groupType: group.groupType,
  color: group.color,
  focusArea: group.focusArea || "",
  meetingDay: group.meetingDay || "",
  meetingTime: group.meetingTime || "",
  location: group.location || "",
  maxCapacity: group.maxCapacity || "",
  leaderId: group.leaderId || "",
  campusId: group.campusId || "",
  startDate: group.startDate || "",
  endDate: group.endDate || "",
  notes: group.notes || "",
  memberIds: group.memberships?.map((m) => m.userId) || [],
});

const matchesSearch = (user, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    user.firstName?.toLowerCase().includes(q) ||
    user.lastName?.toLowerCase().includes(q) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(q) ||
    user.email?.toLowerCase().includes(q)
  );
};

const FormSkeleton = () => (
  <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" aria-busy="true">
    <div className="flex flex-col gap-5 lg:col-span-2">
      <Skeleton className="h-80 w-full rounded-card" />
      <Skeleton className="h-36 w-full rounded-card" />
    </div>
    <Skeleton className="h-96 w-full rounded-card" />
  </div>
);

const ColorPicker = ({ value, onChange }) => (
  <fieldset>
    <legend className="mb-1.5 block text-sm font-medium text-fg">Color</legend>
    <div className="grid grid-cols-4 gap-2 md:grid-cols-8">
      {COLOR_OPTIONS.map((color) => (
        <label key={color.value} className="cursor-pointer" title={color.label}>
          <input
            type="radio"
            name="color"
            value={color.value}
            checked={value === color.value}
            onChange={() => onChange(color.value)}
            className="peer sr-only"
          />
          <span className="sr-only">{color.label}</span>
          <span
            aria-hidden="true"
            className={`flex h-12 items-center justify-center rounded-control transition peer-checked:ring-2 peer-checked:ring-fg peer-checked:ring-offset-2 peer-checked:ring-offset-surface peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface ${swatchTextClass(
              color.value
            )}`}
            style={{ backgroundColor: color.value }}
          >
            {value === color.value && <MdCheck className="h-6 w-6" />}
          </span>
        </label>
      ))}
    </div>
  </fieldset>
);

const CreateEditGroup = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [groupTypes, setGroupTypes] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [initialMemberIds, setInitialMemberIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");

  const formik = useFormik({
    initialValues: EMPTY_GROUP,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          await groupsService.updateGroup(id, values);
          track(EVENTS.GROUP_UPDATED, { group_id: id, group_name: values.name });

          const membersAdded = values.memberIds.filter(
            (memberId) => !initialMemberIds.includes(memberId)
          );
          if (membersAdded.length > 0) {
            track(EVENTS.GROUP_MEMBER_ADDED, {
              group_id: id,
              members_added_count: membersAdded.length,
            });
          }

          notify.success("Group saved");
        } else {
          await groupsService.createGroup(values);
          track(EVENTS.GROUP_CREATED, { group_name: values.name });
          notify.success("Group created");
        }
        navigate("/admin/groups");
      } catch (err) {
        notify.error(getApiErrorMessage(err, "Could not save group"));
      }
    },
  });
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, resetForm } = formik;

  const fetchGroupTypes = useCallback(async () => {
    try {
      const response = await groupsService.getGroupTypes();
      if (response.success) {
        setGroupTypes(response.data);
      }
    } catch (err) {
      // The built-in type list below stands in when the API list is unavailable.
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const response = await AtriumApi.get("/v1/users", {
        params: { per: 1000 },
      });
      setAvailableUsers(response.data.users || []);
    } catch (err) {
      setUsersError(err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await groupsService.getGroupById(id);
      if (response.success) {
        const formValues = toFormValues(response.data);
        resetForm({ values: formValues });
        setInitialMemberIds(formValues.memberIds);
      }
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
    // resetForm is stable for the life of the form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchGroupTypes();
    fetchUsers();
  }, [fetchGroupTypes, fetchUsers]);

  useEffect(() => {
    if (isEdit) {
      fetchGroup();
    }
  }, [isEdit, fetchGroup]);

  const typeOptions = useMemo(
    () =>
      groupTypes.length
        ? groupTypes.map((type) => ({
            value: type.value,
            label: type.emoji ? `${type.emoji} ${type.label}` : type.label,
          }))
        : GROUP_TYPE_OPTIONS,
    [groupTypes]
  );

  const visibleUsers = useMemo(
    () => availableUsers.filter((user) => matchesSearch(user, memberSearch)),
    [availableUsers, memberSearch]
  );

  const toggleMember = (userId) => {
    const next = values.memberIds.includes(userId)
      ? values.memberIds.filter((memberId) => memberId !== userId)
      : [...values.memberIds, userId];
    setFieldValue("memberIds", next);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: `Delete "${values.name}"?`,
      message: "This group and its member list will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      setDeleting(true);
      await groupsService.deleteGroup(id);
      notify.success("Group deleted");
      navigate("/admin/groups");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete group"));
      setDeleting(false);
    }
  };

  const header = (
    <PageHeader
      title={isEdit ? "Edit Group" : "Create Group"}
      description={
        isEdit ? "Update group details and members" : "Set up a new group for your organization"
      }
      backTo={isEdit ? `/admin/groups/${id}` : "/admin/groups"}
      backLabel={isEdit ? "Back to Group" : "Back to Groups"}
      actions={
        isEdit &&
        !loading &&
        !loadError && (
          <Button
            variant="secondary"
            leftIcon={<MdDelete className="h-5 w-5" />}
            loading={deleting}
            onClick={handleDelete}
          >
            Delete Group
          </Button>
        )
      }
    />
  );

  if (loading) {
    return (
      <PageContainer>
        {header}
        <FormSkeleton />
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        {header}
        <ErrorState
          message={getApiErrorMessage(loadError, "We couldn't load this group.")}
          onRetry={fetchGroup}
        />
      </PageContainer>
    );
  }

  let memberList;
  if (usersError) {
    memberList = (
      <ErrorState
        message={getApiErrorMessage(usersError, "We couldn't load people.")}
        onRetry={fetchUsers}
      />
    );
  } else if (loadingUsers) {
    memberList = <Skeleton lines={6} />;
  } else if (visibleUsers.length === 0) {
    memberList = (
      <EmptyState
        variant="inline"
        title={
          memberSearch.trim() ? "No people match your search" : "No people in your organization yet"
        }
      />
    );
  } else {
    memberList = (
      <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto pr-1" aria-label="People">
        {visibleUsers.map((user) => {
          const isSelected = values.memberIds.includes(user.id);
          return (
            <li
              key={user.id}
              className={`rounded-control border px-3 py-2.5 transition-colors ${
                isSelected
                  ? "border-primary bg-brand-50 dark:bg-brand-900/20"
                  : "border-line hover:bg-surface-subtle"
              }`}
            >
              <Checkbox
                label={`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                description={user.email}
                checked={isSelected}
                onChange={() => toggleMember(user.id)}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <PageContainer>
      {header}

      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className="grid grid-cols-1 gap-5 lg:grid-cols-3"
      >
        <div className="flex flex-col gap-5 lg:col-span-2">
          <Card padding="md" className="gap-5">
            <CardHeader title="Basic Information" />
            <InputField
              label="Group Name"
              name="name"
              required
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.name}
              touched={touched.name}
              placeholder="e.g., Youth Group, Volunteers, First Time Visitors"
            />
            <Textarea
              label="Description"
              name="description"
              rows={4}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Describe the purpose and focus of this group…"
            />
            <Select
              label="Group Type"
              name="groupType"
              value={values.groupType}
              onChange={handleChange}
              onBlur={handleBlur}
              options={typeOptions}
            />
          </Card>

          <Card padding="md" className="gap-5">
            <CardHeader title="Customization" />
            <ColorPicker value={values.color} onChange={(color) => setFieldValue("color", color)} />
          </Card>
        </div>

        <Card padding="md" className="gap-4 lg:sticky lg:top-20 lg:self-start">
          <CardHeader
            title={`Members (${values.memberIds.length})`}
            description="Select the people who belong to this group."
          />
          <SearchInput
            value={memberSearch}
            onChange={setMemberSearch}
            delay={150}
            placeholder="Search people…"
            ariaLabel="Search people"
          />
          {memberList}
        </Card>

        <div className="flex justify-end gap-2 lg:col-span-3">
          <Button variant="secondary" onClick={() => navigate("/admin/groups")}>
            Cancel
          </Button>
          <Button type="submit" loading={formik.isSubmitting}>
            {isEdit ? "Save Changes" : "Create Group"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
};

export default CreateEditGroup;
